import Feature from 'ol/Feature';
import {Point} from 'ol/geom';
import {Icon, Stroke, Style} from 'ol/style';
import GeoJSON from 'ol/format/GeoJSON';
import {hexToRGBA} from "@/assets/js/utils";
import {explosion} from "@/animations/effects/explosion";

/**
 * Drone strike animation — a Shahed-style drone flies along the path; on
 * arrival a brief impact flash + fire appears at the target.
 *
 * Expected `data`: GeoJSON FeatureCollection with one LineString feature
 * (optional `properties.speed`).
 */
export function drone(layer, news, geoJson) {
    const features = new GeoJSON().readFeatures(geoJson, {
        dataProjection: 'EPSG:4326',
        featureProjection: 'EPSG:3857'
    });
    if (!features.length) return {getFeature: () => null};

    const pathFeature = features[0];
    pathFeature.setStyle(new Style({
        stroke: new Stroke({
            color: hexToRGBA(news.color, 0.55),
            width: 4,
            lineDash: [4, 6]
        })
    }));
    const coords = pathFeature.getGeometry().getCoordinates();
    const target = coords[coords.length - 1];
    pathFeature.set('where', coords[0]);
    layer.getSource().addFeature(pathFeature);

    const speed = pathFeature.get('speed') || 22;

    const segLen = [];
    let total = 0;
    for (let i = 1; i < coords.length; i++) {
        segLen.push(Math.hypot(coords[i][0] - coords[i - 1][0], coords[i][1] - coords[i - 1][1]));
        total += segLen[i - 1];
    }

    const DRONE_TICK = 40;

    function droneStyle(rotation) {
        return new Style({
            image: new Icon({
                src: '/images/drone.svg',
                scale: 0.5,
                rotation,
                rotateWithView: true
            })
        });
    }

    function headingFor(a, b) {
        return Math.atan2(b[0] - a[0], b[1] - a[1]);
    }

    function launchDrone() {
        if (total === 0) return;
        const duration = total / speed;

        const d = new Feature(new Point(coords[0]));
        d.set('selectable', false);
        d.setStyle(droneStyle(headingFor(coords[0], coords[1])));
        layer.getSource().addFeature(d);

        let elapsed = 0;
        let lastSeg = 0;
        const interval = setInterval(() => {
            elapsed += DRONE_TICK;
            const p = Math.min(1, elapsed / duration);
            const dist = p * total;

            let seg = 0, acc = 0;
            while (seg < segLen.length - 1 && acc + segLen[seg] < dist) {
                acc += segLen[seg];
                seg++;
            }
            const a = coords[seg], b = coords[seg + 1];
            const frac = segLen[seg] > 0 ? (dist - acc) / segLen[seg] : 0;
            d.setGeometry(new Point([
                a[0] + (b[0] - a[0]) * frac,
                a[1] + (b[1] - a[1]) * frac
            ]));

            if (seg !== lastSeg) {
                lastSeg = seg;
                d.setStyle(droneStyle(headingFor(a, b)));
            }

            if (p >= 1) {
                clearInterval(interval);
                layer.getSource().removeFeature(d);
                explosion(layer, target, {
                    color: news.color,
                    maxRadius: 16,
                    fireCount: 3,
                    fireSpread: 350,
                    fireDuration: 2000
                });
            }
        }, DRONE_TICK);
    }

    launchDrone();
    setInterval(launchDrone, 5000);

    return {
        getFeature() {
            return pathFeature;
        }
    };
}
